import { SEO } from '../components/SEO';

export default function PrivacyPage() {
  return (
    <main className="pt-20" data-testid="privacy-page">
      <SEO
        title="Privacy Policy"
        description="Privacy Policy for siddharthkaul.com. Learn how we collect, use, and protect your personal information. GDPR and CCPA compliant."
        canonicalUrl="/privacy"
      />
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-[900px] mx-auto px-6 md:px-12">
          <h1 className="font-['Montserrat'] font-extrabold text-4xl md:text-5xl text-[#2C3E50] mb-6">
            Privacy Policy
          </h1>
          <p className="text-[#64748B] mb-8">
            Last Updated: January 16, 2026
          </p>

          <div className="prose prose-slate max-w-none space-y-8">
            <section>
              <h2 className="font-['Montserrat'] font-bold text-2xl text-[#2C3E50] mb-4">
                1. Introduction
              </h2>
              <p className="text-[#64748B] leading-relaxed mb-4">
                Siddharth Kaul ("I", "me", "my") operates siddharthkaul.com (the "Site"). This Privacy Policy explains how I collect, use, disclose, and safeguard your information when you visit my website or engage with my consulting services.
              </p>
              <p className="text-[#64748B] leading-relaxed">
                By using this Site, you agree to the collection and use of information in accordance with this policy. If you do not agree with the terms of this Privacy Policy, please do not access the Site.
              </p>
            </section>

            <section>
              <h2 className="font-['Montserrat'] font-bold text-2xl text-[#2C3E50] mb-4">
                2. Information I Collect
              </h2>

              <h3 className="font-['Montserrat'] font-semibold text-xl text-[#2C3E50] mb-3 mt-6">
                2.1 Information You Provide
              </h3>
              <p className="text-[#64748B] leading-relaxed mb-3">
                I collect information you voluntarily provide when you:
              </p>
              <ul className="list-disc list-inside text-[#64748B] space-y-2 ml-4">
                <li>Submit contact forms or quote requests</li>
                <li>Subscribe to my newsletter</li>
                <li>Download whitepapers or research materials</li>
                <li>Book consultation calls via Calendly</li>
                <li>Communicate with me via email</li>
              </ul>
              <p className="text-[#64748B] leading-relaxed mt-3">
                This information may include:
              </p>
              <ul className="list-disc list-inside text-[#64748B] space-y-2 ml-4">
                <li>Name and contact information (email, phone)</li>
                <li>Company name and job title</li>
                <li>Project details and business requirements</li>
                <li>Any other information you choose to provide</li>
              </ul>

              <h3 className="font-['Montserrat'] font-semibold text-xl text-[#2C3E50] mb-3 mt-6">
                2.2 Automatically Collected Information
              </h3>
              <p className="text-[#64748B] leading-relaxed mb-3">
                When you visit the Site, I automatically collect certain information about your device and usage:
              </p>
              <ul className="list-disc list-inside text-[#64748B] space-y-2 ml-4">
                <li>IP address and location data</li>
                <li>Browser type and version</li>
                <li>Pages visited and time spent on pages</li>
                <li>Referring website or source</li>
                <li>Device information (operating system, device type)</li>
              </ul>
              <p className="text-[#64748B] leading-relaxed mt-3">
                This information is collected through Google Analytics and similar technologies.
              </p>
            </section>

            <section>
              <h2 className="font-['Montserrat'] font-bold text-2xl text-[#2C3E50] mb-4">
                3. How I Use Your Information
              </h2>
              <p className="text-[#64748B] leading-relaxed mb-3">
                I use the information I collect to:
              </p>
              <ul className="list-disc list-inside text-[#64748B] space-y-2 ml-4">
                <li>Respond to your inquiries and provide consulting services</li>
                <li>Send newsletters and updates (with your consent)</li>
                <li>Improve website functionality and user experience</li>
                <li>Analyze website traffic and usage patterns</li>
                <li>Comply with legal obligations</li>
                <li>Prevent fraud and enhance security</li>
              </ul>
            </section>

            <section>
              <h2 className="font-['Montserrat'] font-bold text-2xl text-[#2C3E50] mb-4">
                4. Information Sharing and Disclosure
              </h2>
              <p className="text-[#64748B] leading-relaxed mb-3">
                I do not sell, rent, or trade your personal information. I may share your information only in the following circumstances:
              </p>
              <ul className="list-disc list-inside text-[#64748B] space-y-2 ml-4">
                <li><strong>Service Providers:</strong> Third-party services that help operate my website (e.g., Vercel for hosting, Google Analytics, Calendly for scheduling)</li>
                <li><strong>Legal Requirements:</strong> When required by law, regulation, or legal process</li>
                <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
                <li><strong>With Your Consent:</strong> Any other disclosure made with your explicit permission</li>
              </ul>
            </section>

            <section>
              <h2 className="font-['Montserrat'] font-bold text-2xl text-[#2C3E50] mb-4">
                5. Cookies and Tracking Technologies
              </h2>
              <p className="text-[#64748B] leading-relaxed mb-3">
                This Site uses cookies and similar tracking technologies to enhance user experience and analyze website traffic. You can control cookies through your browser settings.
              </p>
              <p className="text-[#64748B] leading-relaxed">
                <strong>Types of cookies used:</strong>
              </p>
              <ul className="list-disc list-inside text-[#64748B] space-y-2 ml-4">
                <li><strong>Essential Cookies:</strong> Required for basic site functionality</li>
                <li><strong>Analytics Cookies:</strong> Google Analytics for usage tracking</li>
                <li><strong>Performance Cookies:</strong> Vercel Speed Insights</li>
              </ul>
            </section>

            <section>
              <h2 className="font-['Montserrat'] font-bold text-2xl text-[#2C3E50] mb-4">
                6. Data Security
              </h2>
              <p className="text-[#64748B] leading-relaxed">
                I implement reasonable security measures to protect your personal information from unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the internet is 100% secure, and I cannot guarantee absolute security.
              </p>
            </section>

            <section>
              <h2 className="font-['Montserrat'] font-bold text-2xl text-[#2C3E50] mb-4">
                7. Data Retention
              </h2>
              <p className="text-[#64748B] leading-relaxed">
                I retain your personal information only as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required or permitted by law.
              </p>
            </section>

            <section>
              <h2 className="font-['Montserrat'] font-bold text-2xl text-[#2C3E50] mb-4">
                8. Your Rights
              </h2>
              <p className="text-[#64748B] leading-relaxed mb-3">
                Depending on your location, you may have the following rights:
              </p>
              <ul className="list-disc list-inside text-[#64748B] space-y-2 ml-4">
                <li><strong>Access:</strong> Request a copy of your personal data</li>
                <li><strong>Correction:</strong> Request correction of inaccurate data</li>
                <li><strong>Deletion:</strong> Request deletion of your personal data</li>
                <li><strong>Opt-Out:</strong> Unsubscribe from marketing communications</li>
                <li><strong>Data Portability:</strong> Request transfer of your data</li>
              </ul>
              <p className="text-[#64748B] leading-relaxed mt-3">
                To exercise these rights, please contact me at <a href="mailto:contact@siddharthkaul.com" className="text-[#3498DB] hover:underline">contact@siddharthkaul.com</a>.
              </p>
            </section>

            <section>
              <h2 className="font-['Montserrat'] font-bold text-2xl text-[#2C3E50] mb-4">
                9. Third-Party Links
              </h2>
              <p className="text-[#64748B] leading-relaxed">
                This Site may contain links to third-party websites (LinkedIn, Substack, Calendly, etc.). I am not responsible for the privacy practices of these external sites. Please review their privacy policies before providing any personal information.
              </p>
            </section>

            <section>
              <h2 className="font-['Montserrat'] font-bold text-2xl text-[#2C3E50] mb-4">
                10. Children's Privacy
              </h2>
              <p className="text-[#64748B] leading-relaxed">
                This Site is not intended for individuals under the age of 18. I do not knowingly collect personal information from children. If you believe I have inadvertently collected such information, please contact me immediately.
              </p>
            </section>

            <section>
              <h2 className="font-['Montserrat'] font-bold text-2xl text-[#2C3E50] mb-4">
                11. International Data Transfers
              </h2>
              <p className="text-[#64748B] leading-relaxed">
                Your information may be transferred to and processed in countries other than your country of residence. By using this Site, you consent to such transfers in accordance with this Privacy Policy.
              </p>
            </section>

            <section>
              <h2 className="font-['Montserrat'] font-bold text-2xl text-[#2C3E50] mb-4">
                12. Changes to This Privacy Policy
              </h2>
              <p className="text-[#64748B] leading-relaxed">
                I may update this Privacy Policy from time to time. Changes will be posted on this page with an updated "Last Updated" date. I encourage you to review this policy periodically.
              </p>
            </section>

            <section>
              <h2 className="font-['Montserrat'] font-bold text-2xl text-[#2C3E50] mb-4">
                13. Contact Information
              </h2>
              <p className="text-[#64748B] leading-relaxed mb-3">
                If you have questions or concerns about this Privacy Policy, please contact me:
              </p>
              <div className="bg-[#F8F9FA] rounded-lg p-6">
                <p className="text-[#2C3E50] font-medium">Siddharth Kaul</p>
                <p className="text-[#64748B]">Email: <a href="mailto:contact@siddharthkaul.com" className="text-[#3498DB] hover:underline">contact@siddharthkaul.com</a></p>
                <p className="text-[#64748B]">Website: <a href="https://siddharthkaul.com" className="text-[#3498DB] hover:underline">siddharthkaul.com</a></p>
              </div>
            </section>

            <section className="mt-12 pt-8 border-t border-slate-200">
              <h2 className="font-['Montserrat'] font-bold text-2xl text-[#2C3E50] mb-4">
                GDPR Compliance (EU Visitors)
              </h2>
              <p className="text-[#64748B] leading-relaxed mb-3">
                If you are located in the European Economic Area (EEA), you have additional rights under the General Data Protection Regulation (GDPR):
              </p>
              <ul className="list-disc list-inside text-[#64748B] space-y-2 ml-4">
                <li>Right to withdraw consent at any time</li>
                <li>Right to lodge a complaint with a supervisory authority</li>
                <li>Right to object to processing of your personal data</li>
                <li>Right to restriction of processing</li>
              </ul>
              <p className="text-[#64748B] leading-relaxed mt-3">
                The legal basis for processing your data includes: consent, contractual necessity, legal obligation, and legitimate interests.
              </p>
            </section>

            <section className="mt-8">
              <h2 className="font-['Montserrat'] font-bold text-2xl text-[#2C3E50] mb-4">
                CCPA Compliance (California Residents)
              </h2>
              <p className="text-[#64748B] leading-relaxed mb-3">
                If you are a California resident, you have rights under the California Consumer Privacy Act (CCPA):
              </p>
              <ul className="list-disc list-inside text-[#64748B] space-y-2 ml-4">
                <li>Right to know what personal information is collected</li>
                <li>Right to know whether your personal information is sold or disclosed</li>
                <li>Right to opt-out of the sale of personal information (I do not sell personal information)</li>
                <li>Right to request deletion of personal information</li>
                <li>Right to non-discrimination for exercising CCPA rights</li>
              </ul>
            </section>
          </div>
        </div>
      </section>
    </main>
  );
}
